import { useEffect, useMemo, useState } from 'react';
import { isForfettario } from '../lib/branding';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { FileText, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useNotifications } from '../store/useStore';
import { COMPANY_INFO } from '../lib/letterheadPdf';
import { formatCurrency, getDaysUntilRenewal } from '../lib/subscriptionUtils';
import {
    SUBSCRIPTION_MONTH_OPTIONS,
    createSubscriptionInvoice,
    formatSubscriptionPeriod,
    getSubscriptionInvoicePeriod,
    getSuggestedInvoiceMonths,
    notifySubscriptionDraftsChanged,
    previewSubscriptionInvoiceTotals,
} from '../lib/subscriptionInvoiceUtils';
import type { CustomerSubscription } from './SubscriptionFormModal';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
    subscription: CustomerSubscription | null;
}

type Mode = 'draft' | 'issue';

const formatLongDate = (isoDate: string) => format(parseISO(isoDate), 'd MMMM yyyy', { locale: it });

export function SubscriptionInvoiceModal({ open, onClose, onCreated, subscription }: Props) {
    const { addNotification } = useNotifications();

    const [months, setMonths] = useState(1);
    const [mode, setMode] = useState<Mode>('draft');
    const [noVatForfettario, setNoVatForfettario] = useState(isForfettario());
    const [submitting, setSubmitting] = useState(false);

    // Mesi di partenza: la cadenza del contratto, o di più se il periodo è
    // già iniziato da un pezzo (per coprire in una volta i mesi arretrati).
    useEffect(() => {
        if (!open || !subscription) return;
        setMonths(Math.max(
            subscription.billing_period_months || 1,
            getSuggestedInvoiceMonths(subscription.next_billing_date)
        ));
        setMode('draft');
        setNoVatForfettario(isForfettario());
    }, [open, subscription]);

    // L'inizio del periodo è sempre la prossima fattura del contratto: così
    // due fatture non possono coprire gli stessi giorni.
    const period = useMemo(
        () => subscription
            ? getSubscriptionInvoicePeriod(subscription.next_billing_date, months, subscription.billing_anchor_date)
            : null,
        [subscription, months]
    );

    const totals = useMemo(() => {
        if (!subscription || !period) return null;
        return previewSubscriptionInvoiceTotals(subscription, months, period, noVatForfettario);
    }, [subscription, months, period, noVatForfettario]);

    const overdueDays = subscription ? getDaysUntilRenewal(subscription.next_billing_date) : 0;

    const handleSubmit = async () => {
        if (!subscription || submitting) return;
        setSubmitting(true);
        try {
            const result = await createSubscriptionInvoice({
                subscription,
                months,
                issueNow: mode === 'issue',
                noVatForfettario,
                iban: COMPANY_INFO.iban,
            });

            if (result.issueError) {
                addNotification({
                    type: 'error',
                    title: 'Bozza creata, emissione non riuscita',
                    message: `La bozza è in Fatture: riprova da lì con "Emetti su Fatture in Cloud". ${result.issueError}`,
                    persistent: true,
                });
            } else {
                addNotification({
                    type: 'success',
                    title: result.invoice.invoice_number ? `Fattura ${result.invoice.invoice_number} emessa` : 'Bozza creata',
                    message: `${months} ${months === 1 ? 'mese' : 'mesi'} di abbonamento, ${formatSubscriptionPeriod(result.period)}.`,
                });
            }

            if (result.billingError) {
                addNotification({
                    type: 'error',
                    title: 'Prossima fattura non aggiornata',
                    message: `Il documento c'è, ma la data della prossima fattura non è stata spostata: ${result.billingError}`,
                });
            }

            notifySubscriptionDraftsChanged();
            onCreated();
            onClose();
        } catch (err) {
            addNotification({
                type: 'error',
                title: 'Errore creazione fattura',
                message: (err as Error)?.message ?? 'Impossibile creare la fattura',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" /> Fattura abbonamento
                    </DialogTitle>
                    <DialogDescription>
                        {subscription?.customers?.company_name ?? 'Cliente'} — {formatCurrency(subscription?.monthly_amount ?? 0)}/mese
                    </DialogDescription>
                </DialogHeader>

                {subscription && period && (
                    <div className="space-y-4 py-1">
                        {overdueDays < 0 && (
                            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                                Periodo iniziato da {Math.abs(overdueDays)} giorni e non ancora fatturato
                                (prossima fattura prevista il {formatLongDate(subscription.next_billing_date)}).
                            </div>
                        )}

                        {/* Mesi da fatturare */}
                        <div className="space-y-1">
                            <Label>Mesi da fatturare *</Label>
                            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SUBSCRIPTION_MONTH_OPTIONS.map((m) => (
                                        <SelectItem key={m} value={String(m)}>
                                            {m} {m === 1 ? 'mese' : 'mesi'} — {formatCurrency(subscription.monthly_amount * m)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Periodo */}
                        <div className="rounded border bg-white px-3 py-2 text-sm">
                            <span className="text-gray-500">Periodo fatturato: </span>
                            <span className="font-medium text-gray-900">{formatSubscriptionPeriod(period)}</span>
                            <p className="mt-1 text-xs text-gray-400">
                                Parte dalla prossima fattura del contratto. Dopo, la prossima fattura passa al {formatLongDate(period.end)}.
                            </p>
                        </div>

                        {/* Cosa fare */}
                        <div className="space-y-1">
                            <Label>Cosa fare</Label>
                            <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Crea la bozza, la controllo e la emetto dopo</SelectItem>
                                    <SelectItem value="issue">Emetti subito su Fatture in Cloud</SelectItem>
                                </SelectContent>
                            </Select>
                            {mode === 'issue' && (
                                <p className="text-xs text-gray-500">
                                    Il numero lo assegna Fatture in Cloud, con la data di oggi. L'invio allo SDI si fa poi da Fatture in Cloud.
                                </p>
                            )}
                        </div>

                        {/* Opzioni */}
                        <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={noVatForfettario}
                                onChange={(e) => setNoVatForfettario(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300"
                            />
                            <span>
                                <span className="font-medium">Regime forfettario (senza IVA)</span>
                                <span className="block text-xs text-gray-500">
                                    Se disattivo, viene applicata l'IVA.
                                </span>
                            </span>
                        </label>

                        {/* Riepilogo */}
                        {totals && (
                            <div className="rounded border bg-gray-50 p-3 space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Imponibile ({months} × {formatCurrency(subscription.monthly_amount)})</span>
                                    <span className="font-mono">{formatCurrency(totals.taxable_amount)}</span>
                                </div>
                                {totals.tax_amount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">IVA</span>
                                        <span className="font-mono">{formatCurrency(totals.tax_amount)}</span>
                                    </div>
                                )}
                                {totals.marca_da_bollo_amount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Marca da bollo</span>
                                        <span className="font-mono">{formatCurrency(totals.marca_da_bollo_amount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between border-t pt-1 font-semibold">
                                    <span>Totale fattura</span>
                                    <span className="font-mono text-green-700">{formatCurrency(totals.total_amount)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
                    <Button onClick={handleSubmit} disabled={submitting || !subscription}>
                        {submitting ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {mode === 'issue' ? 'Emissione...' : 'Creazione...'}</>
                        ) : mode === 'issue' ? (
                            'Emetti fattura'
                        ) : (
                            'Crea bozza'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SubscriptionInvoiceModal;
